import React, { useEffect, useRef } from 'react';

import { AuthService } from '../../services/auth.service';
import { queryClient } from '../../services/react-query/react-query.client';
import {
    SESSION_SYNC_ACTIONS,
    SessionSyncEventPayload,
    getCurrentTabId,
    subscribeToSessionSyncEvents,
} from '../../services/session-sync.service';
import { resetAllStores } from '../../store';

const ROOT_PATH = '/';

const shouldProcessSyncEvent = (
    payload: SessionSyncEventPayload,
    currentTabId: string,
    handledEventIds: Set<string>
) => {
    if (payload.action !== SESSION_SYNC_ACTIONS.MASQUERADE_CONTEXT_CHANGED) {
        return false;
    }

    if (payload.originTabId === currentTabId) {
        return false;
    }

    if (handledEventIds.has(payload.id)) {
        return false;
    }

    return true;
};

export const SessionSyncListener: React.FC = () => {
    const isSyncInProgress = useRef(false);
    const handledEventIds = useRef<Set<string>>(new Set());

    useEffect(() => {
        const currentTabId = getCurrentTabId();

        return subscribeToSessionSyncEvents(async (payload) => {
            if (!shouldProcessSyncEvent(payload, currentTabId, handledEventIds.current)) {
                return;
            }

            if (isSyncInProgress.current) {
                return;
            }

            handledEventIds.current.add(payload.id);
            isSyncInProgress.current = true;

            try {
                resetAllStores();
                queryClient.clear();
                await AuthService.whoIam();
            } catch {
                // Hard reload below still guarantees a clean app context.
            } finally {
                window.location.assign(ROOT_PATH);
            }
        });
    }, []);

    return null;
};
